import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { History, User, Package, Folder, Clock, Search, Loader, Download, FileText, ChevronLeft, ChevronRight, Calendar, Tag, ChevronDown } from 'lucide-react';
import Header from '../../components/Header';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';
import { generatePdfReport } from '../../utils/pdfGenerator';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const ACTION_LABELS = {
  create: { label: 'Creación', color: 'bg-green-100 text-green-700' },
  update: { label: 'Edición', color: 'bg-blue-100 text-blue-700' },
  delete: { label: 'Eliminación', color: 'bg-red-100 text-red-700' },
};

const ENTITY_LABELS = {
  product: { label: 'Producto', icon: Package, color: 'text-blue-500' },
  category: { label: 'Categoría', icon: Folder, color: 'text-purple-500' },
  user: { label: 'Usuario', icon: User, color: 'text-green-500' },
  company: { label: 'Empresa', icon: Tag, color: 'text-orange-500' },
};

const ROLE_LABELS = {
  global_admin: 'Global Admin',
  company_admin: 'Administrador',
  agent: 'Agente',
  viewer: 'Visualizador',
};

const ChangesTable = ({ changesJson }) => {
  try {
    const changes = JSON.parse(changesJson);
    const entries = Object.entries(changes);
    if (!entries.length) return <p className="text-xs text-gray-400 italic">Sin detalles registrados</p>;
    return (
      <table className="text-xs w-full border-collapse">
        <thead>
          <tr className="text-left border-b border-gray-200">
            <th className="pb-2 pr-8 font-semibold text-gray-500 uppercase tracking-wide">Campo</th>
            <th className="pb-2 pr-8 font-semibold text-red-500 uppercase tracking-wide">Antes</th>
            <th className="pb-2 font-semibold text-green-600 uppercase tracking-wide">Después</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([field, val]) => (
            <tr key={field} className="border-b border-gray-100 last:border-0">
              <td className="py-1.5 pr-8 font-medium text-gray-700">{field}</td>
              <td className="py-1.5 pr-8">
                <span className="bg-red-50 text-red-700 px-2 py-0.5 rounded text-xs font-mono">
                  {String(val?.antes ?? val?.before ?? '—')}
                </span>
              </td>
              <td className="py-1.5">
                <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded text-xs font-mono">
                  {String(val?.después ?? val?.despues ?? val?.after ?? '—')}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  } catch {
    return <p className="text-xs text-gray-400 italic">No disponible</p>;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('es-CL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const EditHistoryPage = () => {
  const { isAdmin, isGlobalAdmin } = useAuth();
  const navigate = useNavigate();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [days, setDays] = useState(30);
  const [entityType, setEntityType] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  // Debounce: espera 400ms tras el último keystroke antes de buscar
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiService.getEditLogs({ page, limit: 20, days, search, entity_type: entityType });
      if (res.success) {
        setLogs(res.logs || []);
        setPagination(res.pagination);
      }
    } catch (error) {
      console.error('Error cargando historial de cambios:', error);
    } finally {
      setLoading(false);
    }
  }, [page, days, search, entityType]);

  useEffect(() => {
    if (!isAdmin && !isGlobalAdmin) { navigate('/admin'); return; }
    fetchLogs();
    window.addEventListener('tenantChanged', fetchLogs);
    return () => window.removeEventListener('tenantChanged', fetchLogs);
  }, [fetchLogs, isAdmin, isGlobalAdmin, navigate]);

  useEffect(() => { setPage(1); }, [days, search, entityType]);

  const formatChangesForExport = (changesJson, separator = '\n') => {
    if (!changesJson) return '—';
    try {
      const changes = JSON.parse(changesJson);
      const entries = Object.entries(changes);
      if (!entries.length) return '—';
      return entries
        .map(([field, val]) => `${field}: ${val?.antes ?? '—'} → ${val?.después ?? val?.despues ?? '—'}`)
        .join(separator);
    } catch { return '—'; }
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const res = await apiService.getEditLogsAll({ days, search, entity_type: entityType });
      const allLogs = res.logs || [];
      const header = ['Tipo', 'Nombre', 'Acción', 'Modificado por', 'Email', 'Rol', 'Fecha y Hora', 'Detalle de Cambios'].join(',');
      const rows = allLogs.map(l => {
        const entity = ENTITY_LABELS[l.entity_type] || {};
        const action = ACTION_LABELS[l.action] || {};
        return [
          entity.label || l.entity_type || '—',
          l.entity_name || '—',
          action.label || l.action || '—',
          l.user?.name || 'Sistema',
          l.user?.email || '—',
          ROLE_LABELS[l.user?.role] || l.user?.role || '—',
          formatDate(l.edit_date),
          formatChangesForExport(l.changes)
        ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',');
      });
      const csv = [header, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `cambios_${days}dias.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error exportando CSV:', e);
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      setExporting(true);
      const res = await apiService.getEditLogsAll({ days, search, entity_type: entityType });
      const allLogs = res.logs || [];

      const doc = new jsPDF({ orientation: 'portrait' });
      doc.setFontSize(15);
      doc.text(`Historial de Cambios (últimos ${days} días)`, 14, 18);

      // Columnas: Tipo | Nombre / ↳Campo | Acción / Antes | Usuario / Después | Fecha
      const head = [['Tipo', 'Nombre / Campo', 'Acción / Antes', 'Usuario / Después', 'Fecha']];
      const body = [];

      allLogs.forEach(l => {
        // Fila principal
        body.push([
          ENTITY_LABELS[l.entity_type]?.label || l.entity_type || '—',
          l.entity_name || '—',
          ACTION_LABELS[l.action]?.label || l.action || '—',
          l.user?.name || 'Sistema',
          formatDate(l.edit_date)
        ]);
        // Sub-filas de detalle (una por campo cambiado)
        if (l.changes) {
          try {
            const changes = JSON.parse(l.changes);
            Object.entries(changes).forEach(([field, val]) => {
              body.push([
                '__detail__',
                `  ↳ ${field}`,
                String(val?.antes ?? '—'),
                String(val?.después ?? val?.despues ?? '—'),
                ''
              ]);
            });
          } catch { /* sin detalle */ }
        }
      });

      doc.autoTable({
        startY: 26,
        head,
        body,
        theme: 'striped',
        styles: { fontSize: 8, cellPadding: 2.5, overflow: 'linebreak' },
        headStyles: { fillColor: [48, 63, 159], textColor: 255, fontStyle: 'bold', fontSize: 8.5 },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        columnStyles: {
          0: { cellWidth: 22 },
          1: { cellWidth: 55 },
          2: { cellWidth: 40 },
          3: { cellWidth: 40 },
          4: { cellWidth: 33 }
        },
        didParseCell: (data) => {
          if (data.row.raw[0] === '__detail__') {
            // Ocultar el marcador
            if (data.column.index === 0) data.cell.text = [];
            // Fondo suave
            data.cell.styles.fillColor = [248, 250, 255];
            data.cell.styles.fontSize = 7.5;
            // Color por columna: campo=índigo, antes=rojo, después=verde
            if (data.column.index === 1) {
              data.cell.styles.textColor = [79, 70, 229];
              data.cell.styles.fontStyle = 'italic';
            } else if (data.column.index === 2) {
              data.cell.styles.textColor = [180, 30, 30];
            } else if (data.column.index === 3) {
              data.cell.styles.textColor = [22, 101, 52];
            } else {
              data.cell.styles.textColor = [200, 200, 200];
            }
          }
        },
        didDrawPage: (data) => {
          doc.setFontSize(8);
          doc.text(`Página ${doc.internal.getNumberOfPages()}`, data.settings.margin.left, doc.internal.pageSize.height - 8);
        }
      });

      doc.save(`cambios_${days}dias.pdf`);
    } catch (e) {
      console.error('Error exportando PDF:', e);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div className="mb-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h2 className="text-4xl font-bold text-gray-900 mb-2">Historial de Cambios</h2>
          <p className="text-gray-500">Registro persistente de todas las creaciones, ediciones y eliminaciones del sistema.</p>
        </motion.div>

        <motion.div
          className="bg-white/80 backdrop-blur-xl rounded-2xl p-5 mb-6 shadow-md border border-white/20 flex flex-wrap gap-3 items-center justify-between"
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        >
          <div className="flex flex-wrap gap-3 flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="Buscar por nombre, producto o usuario..."
                className="pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:ring-2 focus:ring-blue-300 focus:outline-none w-64"
              />
            </div>
            <select
              value={days}
              onChange={e => setDays(Number(e.target.value))}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:ring-2 focus:ring-blue-300 focus:outline-none"
            >
              <option value={1}>Hoy (1 día)</option>
              <option value={7}>Últimos 7 días</option>
              <option value={30}>Últimos 30 días</option>
              <option value={60}>Últimos 60 días</option>
              <option value={90}>Últimos 90 días</option>
              <option value={365}>Último año</option>
            </select>
            <select
              value={entityType}
              onChange={e => setEntityType(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:ring-2 focus:ring-blue-300 focus:outline-none"
            >
              <option value="">Todos los tipos</option>
              <option value="product">Productos</option>
              <option value="category">Categorías</option>
              <option value="user">Usuarios</option>
              <option value="company">Empresas</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleExportPDF}
              disabled={exporting}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {exporting ? <Loader className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              PDF
            </button>
            <button
              onClick={handleExportCSV}
              disabled={exporting}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {exporting ? <Loader className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              CSV
            </button>
          </div>
        </motion.div>

        <motion.div
          className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-md border border-white/20 overflow-hidden"
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
        >
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader className="w-10 h-10 text-blue-500 animate-spin" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-gray-500 uppercase text-xs">
                      <th className="py-3 px-5 text-left">Tipo</th>
                      <th className="py-3 px-5 text-left">Nombre</th>
                      <th className="py-3 px-5 text-left">Acción</th>
                      <th className="py-3 px-5 text-left">Modificado por</th>
                      <th className="py-3 px-5 text-left">Fecha y Hora</th>
                      <th className="py-3 px-3 text-center">Detalles</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <AnimatePresence>
                      {logs.length > 0 ? logs.map((log, i) => {
                        const entity = ENTITY_LABELS[log.entity_type] || { label: log.entity_type, icon: History, color: 'text-gray-500' };
                        const EntityIcon = entity.icon;
                        const actionInfo = ACTION_LABELS[log.action] || { label: log.action, color: 'bg-gray-100 text-gray-600' };
                        return (
                          <React.Fragment key={log.id}>
                          <motion.tr
                            className={`transition-colors cursor-pointer ${expandedRow === log.id ? 'bg-blue-50/60' : 'hover:bg-blue-50/40'}`}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.02 }}
                            onClick={() => log.changes && setExpandedRow(expandedRow === log.id ? null : log.id)}
                          >
                            <td className="py-3 px-5">
                              <div className="flex items-center gap-2">
                                <EntityIcon className={`w-4 h-4 ${entity.color}`} />
                                <span className="font-medium text-gray-700">{entity.label}</span>
                              </div>
                            </td>
                            <td className="py-3 px-5 font-medium text-gray-900">{log.entity_name || '—'}</td>
                            <td className="py-3 px-5">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${actionInfo.color}`}>
                                {actionInfo.label}
                              </span>
                            </td>
                            <td className="py-3 px-5">
                              {log.user ? (
                                <div className="flex items-center gap-2">
                                  {log.user.avatar ? (
                                    <img src={log.user.avatar} alt="" className="w-7 h-7 rounded-full object-cover border border-gray-200" onError={e => e.target.style.display = 'none'} />
                                  ) : (
                                    <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                                      <User className="w-3.5 h-3.5 text-indigo-500" />
                                    </div>
                                  )}
                                  <div>
                                    <p className="font-medium text-gray-900 text-xs">{log.user.name}</p>
                                    <p className="text-xs text-gray-400">{log.user.email}</p>
                                  </div>
                                </div>
                              ) : (
                                <span className="text-gray-400 text-xs italic">Sistema</span>
                              )}
                            </td>
                            <td className="py-3 px-5">
                              <div className="flex items-center gap-1 text-gray-600 text-xs">
                                <Calendar className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                {formatDate(log.edit_date)}
                              </div>
                            </td>
                            <td className="py-3 px-3 text-center">
                              {log.changes ? (
                                <ChevronDown
                                  className={`w-4 h-4 text-blue-400 mx-auto transition-transform duration-200 ${expandedRow === log.id ? 'rotate-180' : ''}`}
                                />
                              ) : (
                                <span className="text-gray-300 text-xs">—</span>
                              )}
                            </td>
                          </motion.tr>
                          {expandedRow === log.id && log.changes && (
                            <tr key={`${log.id}-detail`} className="bg-indigo-50/50">
                              <td colSpan={6} className="px-10 py-4 border-b border-indigo-100">
                                <p className="text-xs font-bold text-indigo-500 uppercase tracking-widest mb-3">Detalle del cambio</p>
                                <ChangesTable changesJson={log.changes} />
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        );
                      }) : (
                        <tr>
                          <td colSpan={6} className="py-16 text-center text-gray-400">
                            No hay registros de cambios para el período seleccionado.
                          </td>
                        </tr>
                      )}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>

              {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100 text-sm text-gray-500">
                  <span>
                    Mostrando {((page - 1) * 20) + 1}–{Math.min(page * 20, pagination.total)} de {pagination.total} registros
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="font-medium text-gray-700">Pág. {page} / {pagination.totalPages}</span>
                    <button
                      onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                      disabled={!pagination.hasMore}
                      className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default EditHistoryPage;